import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { useClientApi } from '@/plugins/api/lk';
import { decorateApiActions } from '@/shared/lib';
import type {
  CityResponse,
  CitySelectType,
} from '@/types/Cities';

import { useGlobalStore } from './global';

import type { GlobalCity } from './global';
import type { ComputedRef } from 'vue';

type CitySlugToNamePrepositionalMap = Record<string, string | undefined>;

export const useCitiesStore = defineStore('cities', () => {
  const rawCities = ref<Array<CityResponse>>([]);
  const cities = computed<Array<CitySelectType>>(
    () => rawCities.value.map(
      (rawCity) => ({
        ...rawCity,
        text: rawCity.name,
        value: rawCity.id,
        citySlug: rawCity.slug,
      }),
    ),
  );

  const globalStore = useGlobalStore();
  const citiesGlobal: ComputedRef<Array<GlobalCity>> = computed(() => globalStore.getGlobalCities);

  function getPrepositionalNames () {
    const result: CitySlugToNamePrepositionalMap = {};
    for (const city of citiesGlobal.value) {
      result[city.slug] = city.namePrepositional;
    }

    return result;
  }

  const prepositionalNames = computed(() => getPrepositionalNames());

  function getCityById (cityId: number | null | undefined): CitySelectType | undefined {
    return cities.value.find(({ id }) => id === cityId);
  }

  function getCityByGlobalId (cityGlobalId: string | null | undefined): CitySelectType | undefined {
    return cities.value.find(({ globalId }) => globalId === cityGlobalId);
  }

  function getCityBySlug (citySlug?: string): CityResponse | undefined {
    return rawCities.value.find(({ slug }) => slug === citySlug);
  }

  function getCitiesBySlugs (slugs: Array<string>) {
    return rawCities.value.filter((city) => slugs.includes(city.slug));
  }

  let citiesPromise: Promise<void> | undefined;

  async function getCities (forceReload = false): Promise<void> {
    if (rawCities.value.length && !forceReload) {
      return;
    }

    if (citiesPromise && !forceReload) {
      return citiesPromise;
    }

    const url = 'api/cities';
    const promise = useClientApi()
      .get<Array<CityResponse>>(url)
      .then((response) => {
        rawCities.value = response.data;
      })
      .finally(() => {
        if (citiesPromise === promise) {
          citiesPromise = undefined;
        }
      });

    citiesPromise = promise;

    return promise;
  }

  return {
    getCityById,
    getCityByGlobalId,
    getCityBySlug,
    getCitiesBySlugs,
    cities,
    rawCities,
    prepositionalNames,
    citiesGlobal,
    getPrepositionalNameBySlug (slug: string) {
      return prepositionalNames.value[slug];
    },
    ...decorateApiActions({
      getCities,
    }, 'store/cities.ts'),
  };
});
